import type { AssetManifest, CinematicId } from '@shefira/shared';
import type Phaser from 'phaser';

import { loadLazyAtlases } from '../../assets/manifest-loader.js';
import { cue } from '../../audio/cues.js';
import { PixelText } from '../pixel-text.js';
import type { ShakeKind } from '../screen-shake.js';
import { puffSmoke } from '../smoke-puff.js';
import { computeViewport } from '../viewport.js';
import { DRAGON_ART, DRAGON_CLIPS } from './dragon-clips.js';
import { InputLock } from './input-lock.js';
import { CINEMATICS, type CinematicScript, type Point } from './scripts.js';
import { shadowTexture, silhouetteTexture } from './shadow-texture.js';
import {
  BREACH_VIEW_HEIGHT,
  cuesBetween,
  dragonAt,
  duskAt,
  type Framing,
  framing,
  leanAt,
  puffsBetween,
  shadowAt,
  shakesBetween,
  titleShown,
} from './timeline.js';

/**
 * Plays one of Shefira's scenes (ADR 0067) in the world, before the player:
 * the world dims, her shadow glides over the ground, she comes down, lands,
 * roars, flies off. Input is held for the length of it. Purely visual: the
 * server said which scene, and nothing is sent back.
 *
 * She and her shadow are laid in world pixels around a ground point a few
 * tiles north of the player, and the camera leans north to frame her
 * (`cameraOffset`, added where the scene centres on the player); the dusk is
 * pinned to the view like the teleport's veil. Frames are set by the
 * script's clock, never by Phaser's animations, so the scene plays exactly
 * as its timeline (and its offline check) says. Her clips are lazy atlases,
 * fetched when a scene is asked for; if they fail to come, the silhouette
 * drawn by code stands in for her shadow and she herself is not shown.
 */

type Sheets = AssetManifest['sheets'];

/** Her art must arrive within this, or the scene plays with the stand-in. */
const LOAD_TIMEOUT_MS = 5000;
/** Just under the teleport's veil (1 000 000), above the world, the plates and the markers. */
const DEPTH = 999_000;
const DUSK_COLOUR = 0x05060c;
const TITLE_COLOUR = 0xff6a2a;
const TITLE_SCALE = 3;
/** Clouds of dust along her feet at the impact. */
const PUFFS = 7;

interface Playing {
  script: CinematicScript;
  startedAt: number;
  /** The clock of the last frame drawn, for the shakes, sounds and dust due since. */
  last: number;
  /** The player's feet when it began: the scene is laid around them. */
  player: Point;
  ground: Point;
  framing: Framing;
  layer: Phaser.GameObjects.Container;
  dusk: Phaser.GameObjects.Rectangle;
  dragon: Phaser.GameObjects.Image | undefined;
  shadow: Phaser.GameObjects.Image;
  title: PixelText | undefined;
  /** Put the zoom back when the scene pulled it out to fit her. */
  restoreZoom: (() => void) | undefined;
  done: () => void;
}

export class CinematicPlayer {
  private playing: Playing | undefined;
  private starting = false;
  private readonly lock = new InputLock();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly manifest: Pick<AssetManifest, 'atlases' | 'sheets'>,
    private readonly jolt: (kind: ShakeKind) => void,
  ) {}

  get active(): boolean {
    return this.playing !== undefined || this.starting;
  }

  /** Play a scene around the player's feet; resolves when it is over. A scene asked for while one plays is not stacked. */
  async play(id: CinematicId, player: Point): Promise<void> {
    if (this.active) return;
    this.starting = true;
    this.lock.hold();
    this.scene.input.enabled = false;
    const loaded = await this.loadArt(CINEMATICS[id]);
    this.starting = false;
    if (!this.scene.sys.isActive()) {
      this.release();
      return;
    }
    await new Promise<void>((resolve) => {
      this.begin(id, { x: Math.round(player.x), y: Math.round(player.y) }, loaded, resolve);
    });
  }

  /** The camera's lean this frame, whole pixels; added where the scene centres on the player. */
  cameraOffset(): Point {
    const playing = this.playing;
    if (playing === undefined) return { x: 0, y: 0 };
    const t = this.scene.time.now - playing.startedAt;
    return leanAt(playing.script, t, playing.framing.camera);
  }

  /** Called every frame, after the camera moved. */
  update(): void {
    const playing = this.playing;
    if (playing === undefined) return;
    const { script, ground } = playing;
    const t = this.scene.time.now - playing.startedAt;
    if (t >= script.ms) {
      this.finish();
      return;
    }
    for (const kind of shakesBetween(script, playing.last, t)) this.jolt(kind);
    for (const due of cuesBetween(script, playing.last, t)) cue(due.sound, due.volume);
    for (const puff of puffsBetween(script, playing.last, t)) this.dust(ground, puff.spread);
    playing.last = t;

    const view = this.scene.cameras.main.worldView;
    playing.layer.setPosition(Math.round(view.x), Math.round(view.y));
    playing.dusk.setSize(Math.ceil(view.width), Math.ceil(view.height)).setAlpha(duskAt(script, t));

    const pose = dragonAt(script, t);
    if (playing.dragon !== undefined) {
      playing.dragon.setVisible(pose !== undefined);
      const sheet = pose === undefined ? undefined : this.sheets[DRAGON_CLIPS[pose.clip].art];
      // Each clip sits in its own atlas: the image switches texture with the clip.
      if (pose !== undefined && sheet !== undefined) {
        playing.dragon
          .setTexture(sheet.atlas, `${sheet.prefix}/${String(pose.frame)}`)
          .setPosition(ground.x + pose.x, ground.y + pose.y);
      }
    }
    this.drawShadow(playing, t);

    const title = playing.title;
    if (title !== undefined) {
      const shown = titleShown(script, t);
      title.setVisible(shown);
      // Its glyphs are laid around x = 0; the whole scale keeps them whole pixels.
      if (shown)
        title.container.setPosition(playing.player.x, playing.player.y + playing.framing.titleY);
    }
  }

  destroy(): void {
    this.finish();
    this.release();
  }

  private get sheets(): Sheets {
    return this.manifest.sheets;
  }

  /** The atlases of the clips a scene shows, fetched once; true when every one came. */
  private async loadArt(script: CinematicScript): Promise<boolean> {
    const wanted = script.dragon.length > 0 ? DRAGON_ART : [DRAGON_CLIPS.shadow.art];
    const atlases = wanted
      .map((art) => this.sheets[art]?.atlas)
      .filter((atlas): atlas is string => atlas !== undefined);
    if (atlases.length < wanted.length) return false;
    const timeout = new Promise<Set<string>>((resolve) => {
      window.setTimeout(() => {
        resolve(new Set());
      }, LOAD_TIMEOUT_MS);
    });
    const loaded = await Promise.race([
      loadLazyAtlases(this.scene, this.manifest, atlases),
      timeout,
    ]);
    return atlases.every((atlas) => loaded.has(atlas));
  }

  private begin(id: CinematicId, player: Point, art: boolean, done: () => void): void {
    const script = CINEMATICS[id];
    const scene = this.scene;
    const restoreZoom = script.frame ? this.fitView() : undefined;
    const ground = { x: player.x, y: player.y - script.north };
    const layer = scene.add.container(0, 0).setDepth(DEPTH);
    const dusk = scene.add.rectangle(0, 0, 1, 1, DUSK_COLOUR, 1).setOrigin(0, 0).setAlpha(0);
    layer.add(dusk);
    // Under the dusk: it lies on the ground, and the ground goes dark with it.
    const shadow = scene.add
      .image(0, 0, this.placeholder())
      .setOrigin(0, 0)
      .setDepth(DEPTH - 1)
      .setVisible(false);
    const idle = this.sheets[DRAGON_CLIPS.idle.art];
    const dragon =
      art && script.dragon.length > 0 && idle !== undefined
        ? scene.add
            .image(0, 0, idle.atlas)
            .setOrigin(0, 0)
            .setDepth(DEPTH + 1)
            .setVisible(false)
        : undefined;
    this.playing = {
      script,
      startedAt: scene.time.now,
      last: 0,
      player,
      ground,
      framing: framing(script, scene.scale.height),
      layer,
      dusk,
      dragon,
      shadow,
      title: this.titleFor(script),
      restoreZoom,
      done,
    };
    this.update();
  }

  private titleFor(script: CinematicScript): PixelText | undefined {
    const font = this.sheets['font.pixel'];
    if (script.title === undefined || font === undefined) return undefined;
    const title = new PixelText({
      scene: this.scene,
      atlasKey: font.atlas,
      fontKey: font.prefix,
      color: TITLE_COLOUR,
      outlineColor: 0x12121c,
    });
    title.setText(script.title.text);
    title.container.setScale(TITLE_SCALE).setDepth(DEPTH + 3);
    title.setVisible(false);
    return title;
  }

  /**
   * Her canvas is 224 px tall and she lands three tiles from the player: a
   * view shorter than that (a laptop at ×3, a phone at ×2) pulls back to
   * the largest whole zoom that holds her, for the length of the scene.
   */
  private fitView(): (() => void) | undefined {
    const scale = this.scene.scale;
    if (scale.height >= BREACH_VIEW_HEIGHT) return undefined;
    const zoom = Math.max(1, Math.floor(window.innerHeight / BREACH_VIEW_HEIGHT));
    if (zoom >= scale.zoom) return undefined;
    scale.setZoom(zoom);
    scale.resize(Math.floor(window.innerWidth / zoom), Math.floor(window.innerHeight / zoom));
    return () => {
      const viewport = computeViewport(window.innerWidth, window.innerHeight);
      scale.setZoom(viewport.zoom);
      scale.resize(viewport.width, viewport.height);
    };
  }

  private drawShadow(playing: Playing, t: number): void {
    const pose = shadowAt(playing.script, t);
    const shadow = playing.shadow;
    shadow.setVisible(pose !== undefined);
    if (pose === undefined) return;
    const { feet } = DRAGON_CLIPS.shadow;
    const sheet = this.sheets[DRAGON_CLIPS.shadow.art];
    const texture =
      sheet !== undefined && this.scene.textures.exists(sheet.atlas)
        ? shadowTexture(this.scene, sheet, pose.frame, pose.scale)
        : undefined;
    if (texture !== undefined) {
      if (shadow.texture.key !== texture) shadow.setTexture(texture);
      shadow
        .setScale(1)
        .setAlpha(pose.alpha)
        .setPosition(
          playing.ground.x + pose.x - Math.round(feet.x * pose.scale),
          playing.ground.y + pose.y - Math.round(feet.y * pose.scale),
        );
      return;
    }
    // The stand-in, at a whole magnification, centred on the same ground point.
    const magnify = Math.max(1, Math.round(pose.scale * 3));
    shadow
      .setTexture(this.placeholder())
      .setScale(magnify)
      .setAlpha(Math.min(0.6, pose.alpha * 1.5));
    shadow.setPosition(
      playing.ground.x + pose.x - Math.round((shadow.width * magnify) / 2),
      playing.ground.y + pose.y - Math.round((shadow.height * magnify) / 2),
    );
  }

  /** Dust along her feet at the impact: the rider's puff, spread wide. */
  private dust(ground: Point, spread: number): void {
    for (let index = 0; index < PUFFS; index += 1) {
      const x = ground.x - spread / 2 + Math.round((spread * index) / (PUFFS - 1));
      puffSmoke(this.scene, { x, y: ground.y + 2 }, DEPTH + 2);
    }
  }

  private finish(): void {
    const playing = this.playing;
    if (playing === undefined) return;
    this.playing = undefined;
    playing.layer.destroy(true);
    playing.dragon?.destroy();
    playing.shadow.destroy();
    playing.title?.destroy();
    playing.restoreZoom?.();
    this.release();
    playing.done();
  }

  private release(): void {
    this.lock.release();
    this.scene.input.enabled = true;
  }

  private placeholder(): string {
    return silhouetteTexture(this.scene);
  }
}
